import * as React from "react"
import { createPortal } from "react-dom"
import { KEYS } from "platejs"
import { Editor, Range } from "slate"
import { Plate, PlateContent, PlateElement, usePlateEditor, useEditorRef } from "platejs/react"
import {
  BoldPlugin,
  ItalicPlugin,
  UnderlinePlugin,
  StrikethroughPlugin,
  BlockquotePlugin,
} from "@platejs/basic-nodes/react"
import {
  BulletedListPlugin,
  NumberedListPlugin,
  ListItemPlugin,
  ListItemContentPlugin,
  ListPlugin,
} from "@platejs/list-classic/react"
import { toggleList, unwrapList } from "@platejs/list-classic"
import {
  CodeBlockPlugin,
  CodeLinePlugin,
} from "@platejs/code-block/react"
import { insertCodeBlock } from "@platejs/code-block"
import { MentionPlugin, MentionInputPlugin } from "@platejs/mention/react"
import { SlashPlugin, SlashInputPlugin } from "@platejs/slash-command/react"
import { AutoformatPlugin } from "@platejs/autoformat"
import {
  ListIcon,
  ListOrderedIcon,
  MessageSquareQuote,
  CodeXml,
  Type,
  Hash,
  Mail,
  Phone,
  Link,
  DollarSign,
  Percent,
  MapPin,
  Calendar,
  Building2,
  ToggleRight,
  User,
  AtSign,
  Globe,
  Briefcase,
  Clock,
  Flag,
} from "lucide-react"
import { motion } from "framer-motion"
import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"

// Pre-format function to unwrap existing lists
const preFormat = (editor) => unwrapList(editor)

// Format function for lists
const formatList = (editor, elementType) => {
  if (editor.selection) {
    toggleList(editor, { type: elementType })
  }
}

// Autoformat rules for markdown-like syntax
const autoformatRules = [
  // Block rules - lists (must use format function with toggleList)
  {
    mode: "block",
    type: KEYS.li,
    match: ["* ", "- ", "+ "],
    preFormat,
    format: (editor) => formatList(editor, KEYS.ulClassic),
  },
  {
    mode: "block",
    type: KEYS.li,
    match: ["1. ", "1) "],
    preFormat,
    format: (editor) => formatList(editor, KEYS.olClassic),
  },
  // Block rules - blockquote
  {
    mode: "block",
    type: KEYS.blockquote,
    match: "> ",
    preFormat,
  },
  // Block rules - code block (uses custom format to create proper nested structure)
  {
    mode: "block",
    type: KEYS.codeBlock,
    match: "```",
    format: (editor) => {
      insertCodeBlock(editor)
    },
  },
  // Mark rules - inline formatting
  {
    mode: "mark",
    type: [KEYS.bold, KEYS.italic],
    match: "***",
  },
  {
    mode: "mark",
    type: [KEYS.underline, KEYS.italic],
    match: "__*",
  },
  {
    mode: "mark",
    type: KEYS.bold,
    match: "**",
  },
  {
    mode: "mark",
    type: KEYS.underline,
    match: "__",
  },
  {
    mode: "mark",
    type: KEYS.italic,
    match: "*",
  },
  {
    mode: "mark",
    type: KEYS.italic,
    match: "_",
  },
  {
    mode: "mark",
    type: KEYS.strikethrough,
    match: "~~",
  },
]

// Default empty value for the editor
const emptyValue = [{ type: "p", children: [{ text: "" }] }]

// Parse stored value (JSON string) to Plate Value format
function parseValue(str) {
  if (!str || str.trim() === "") {
    return emptyValue
  }

  // Try to parse as JSON (rich text format)
  try {
    const parsed = JSON.parse(str)
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed
    }
  } catch {
    // Not valid JSON, treat as plain text for backwards compatibility
  }

  // Fall back to treating as plain text (for existing plain text data)
  const paragraphs = str.split("\n").map((line) => ({
    type: "p",
    children: [{ text: line }],
  }))

  return paragraphs
}

// Serialize Plate Value to JSON string for storage
function serializeValue(value) {
  if (!value || !Array.isArray(value)) {
    return ""
  }

  // Check if content is empty (single empty paragraph)
  if (
    value.length === 1 &&
    value[0].type === "p" &&
    value[0].children?.length === 1 &&
    value[0].children[0].text === ""
  ) {
    return ""
  }

  return JSON.stringify(value)
}

// Custom components for rendering
function BulletedListElement(props) {
  return <PlateElement as="ul" className="list-disc pl-6 my-1" {...props} />
}

function NumberedListElement(props) {
  return <PlateElement as="ol" className="list-decimal pl-6 my-1" {...props} />
}

function ListItemElement(props) {
  return <PlateElement as="li" {...props} />
}

function BlockquoteElement(props) {
  return (
    <PlateElement
      as="blockquote"
      className="border-l-4 border-slate-200 pl-4 my-2"
      {...props}
    />
  )
}

function CodeBlockElement({ children, ...props }) {
  return (
    <PlateElement
      as="pre"
      className="bg-muted/50 rounded-md my-2 font-mono text-sm"
      {...props}
    >
      <ScrollArea orientation="horizontal">
        {children}
      </ScrollArea>
    </PlateElement>
  )
}

function CodeLineElement(props) {
  return <PlateElement as="div" className="px-4 w-fit min-w-full first:pt-4 last:pb-4" {...props} />
}

// Data type items for the data field @ menu
const DATA_TYPE_ITEMS = [
  // Text types
  { key: "text", text: "Text", icon: Type, category: "Text" },
  { key: "name", text: "Name", icon: User, category: "Text" },
  { key: "email", text: "Email", icon: Mail, category: "Text" },
  { key: "phone", text: "Phone", icon: Phone, category: "Text" },
  { key: "url", text: "URL", icon: Link, category: "Text" },

  // Numeric types
  { key: "number", text: "Number", icon: Hash, category: "Numeric" },
  { key: "currency", text: "Currency", icon: DollarSign, category: "Numeric" },
  { key: "percentage", text: "Percentage", icon: Percent, category: "Numeric" },

  // Date & Time
  { key: "date", text: "Date", icon: Calendar, category: "Date & Time" },
  { key: "time", text: "Time", icon: Clock, category: "Date & Time" },

  // Location
  { key: "address", text: "Address", icon: MapPin, category: "Location" },
  { key: "city", text: "City", icon: Building2, category: "Location" },
  { key: "state", text: "State/region", icon: Flag, category: "Location" },
  { key: "country", text: "Country", icon: Globe, category: "Location" },
  { key: "postal_code", text: "Postal code", icon: Mail, category: "Location" },

  // Business
  { key: "company", text: "Company", icon: Building2, category: "Business" },
  { key: "job_title", text: "Job title", icon: Briefcase, category: "Business" },
  { key: "industry", text: "Industry", icon: Building2, category: "Business" },

  // Other
  { key: "boolean", text: "Yes/no", icon: ToggleRight, category: "Other" },
]

// Icon lookup map for rendering mentions
const MENTION_ICON_MAP = DATA_TYPE_ITEMS.reduce((acc, item) => {
  acc[item.key] = item.icon
  acc[item.text] = item.icon
  return acc
}, {})

// Context for passing live extracted values to MentionElement without re-inserting nodes
const ExtractedValuesContext = React.createContext({})

const MENTION_COLOR_SCHEMES = {
  sky: {
    badge: "bg-sky-100 dark:bg-sky-900 text-sky-600 dark:text-sky-200",
    description: "text-sky-400 dark:text-sky-400",
  },
  orange: {
    badge: "bg-orange-100 dark:bg-orange-900 text-orange-600 dark:text-orange-200",
    description: "text-orange-400 dark:text-orange-400",
  },
}

function createMentionElement(colorScheme = "orange") {
  const colors = MENTION_COLOR_SCHEMES[colorScheme] || MENTION_COLOR_SCHEMES.orange

  return function MentionElement(props) {
    const { element, children } = props
    const extractedValues = React.useContext(ExtractedValuesContext)
    const Icon = MENTION_ICON_MAP[element.dataType] || MENTION_ICON_MAP[element.key] || MENTION_ICON_MAP[element.value] || AtSign

    // Prefer live value from context (updates when test email changes); fall back to baked-in description
    const displayValue = extractedValues[element.value] ?? element.description

    return (
      <PlateElement
        as="span"
        className={cn(`inline-flex gap-1 ${colors.badge} rounded px-1.5 py-0.5`, "[pre_&]:text-[13.5px]")}
        contentEditable={false}
        {...props}
      >
        <Icon className="size-3.5 shrink-0 mt-0.75 [pre_&]:mt-0.5" />
        <span className="font-medium">{element.value}</span>
        {displayValue && (
          <span className={`${colors.description} pl-0.5`}>{displayValue}</span>
        )}
        {children}
      </PlateElement>
    )
  }
}

// Hook to close a menu when clicking outside the trigger and dropdown
function useClickOutside(refs, onClose) {
  React.useEffect(() => {
    const handleMouseDown = (e) => {
      const isOutside = refs.every(
        (ref) => !ref.current || !ref.current.contains(e.target)
      )
      if (isOutside) onClose()
    }
    document.addEventListener("mousedown", handleMouseDown)
    return () => document.removeEventListener("mousedown", handleMouseDown)
  }, [refs, onClose])
}

// Hook to position a portal dropdown beneath a trigger element.
// Updates the dropdown's DOM style directly to avoid React re-renders during scroll.
function usePortalPosition(triggerRef, dropdownRef) {
  React.useEffect(() => {
    const update = () => {
      if (!triggerRef.current || !dropdownRef.current) return
      const rect = triggerRef.current.getBoundingClientRect()
      const gap = 4
      const padding = 8
      const top = rect.bottom + gap
      const maxHeight = Math.max(120, window.innerHeight - top - padding)
      const el = dropdownRef.current
      el.style.top = `${top}px`
      el.style.left = `${rect.left}px`
      el.style.maxHeight = `${maxHeight}px`
    }
    // Initial position after portal mounts
    requestAnimationFrame(update)
    // Update position on scroll/resize anywhere in the page
    window.addEventListener("scroll", update, true)
    window.addEventListener("resize", update)
    return () => {
      window.removeEventListener("scroll", update, true)
      window.removeEventListener("resize", update)
    }
  }, [triggerRef, dropdownRef])
}

// Create the MentionInputElement component with customizable items and color
function createMentionInputElement(mentionItems, colorScheme = "orange") {
  const items = mentionItems ?? []
  const colors = MENTION_COLOR_SCHEMES[colorScheme] || MENTION_COLOR_SCHEMES.orange

  return function MentionInputElement(props) {
    const { element, children } = props
    const editor = useEditorRef()
    const inputRef = React.useRef(null)
    const triggerRef = React.useRef(null)
    const dropdownRef = React.useRef(null)
    const [searchValue, setSearchValue] = React.useState("")
    const [selectedIndex, setSelectedIndex] = React.useState(0)
    usePortalPosition(triggerRef, dropdownRef)

    // Filter mentionables based on search (search in text, category, and description)
    const filteredItems = items.filter((item) => {
      const search = searchValue.toLowerCase()
      return (
        item.text.toLowerCase().includes(search) ||
        (item.category && item.category.toLowerCase().includes(search)) ||
        (item.description && item.description.toLowerCase().includes(search))
      )
    })

    // Reset selected index when filtered items change
    React.useEffect(() => {
      setSelectedIndex(0)
    }, [searchValue])

    // Focus the input when mounted
    React.useEffect(() => {
      if (inputRef.current) {
        inputRef.current.focus()
      }
    }, [])

    const closeMenu = React.useCallback(() => {
      editor.tf.removeNodes({
        at: [],
        match: (n) => n.type === "mention_input",
      })
      editor.tf.focus()
    }, [editor])

    useClickOutside([triggerRef, dropdownRef], closeMenu)

    const handleSelect = React.useCallback((item) => {
      // Remove the mention input node and insert a mention
      editor.tf.removeNodes({
        at: [],
        match: (n) => n.type === "mention_input",
      })
      const mentionNode = {
        type: "mention",
        key: item.key,
        value: item.text,
        children: [{ text: "" }],
      }
      if (item.dataType) mentionNode.dataType = item.dataType
      if (item.description) mentionNode.description = item.description
      editor.tf.insertNodes(mentionNode)
      // Move cursor after the mention and refocus the editor
      editor.tf.move({ distance: 1 })
      // Use requestAnimationFrame to ensure DOM has settled after portal unmount
      requestAnimationFrame(() => editor.tf.focus())
    }, [editor])

    // Handle keyboard navigation
    const handleKeyDown = React.useCallback(
      (e) => {
        if (e.key === "ArrowDown") {
          e.preventDefault()
          setSelectedIndex((prev) =>
            prev < filteredItems.length - 1 ? prev + 1 : 0
          )
        } else if (e.key === "ArrowUp") {
          e.preventDefault()
          setSelectedIndex((prev) =>
            prev > 0 ? prev - 1 : filteredItems.length - 1
          )
        } else if (e.key === "Enter") {
          e.preventDefault()
          if (filteredItems.length > 0) {
            handleSelect(filteredItems[selectedIndex])
          }
        } else if (e.key === "Escape") {
          e.preventDefault()
          closeMenu()
        } else if (e.key === "Backspace" && searchValue === "") {
          e.preventDefault()
          closeMenu()
        }
      },
      [filteredItems, selectedIndex, handleSelect, closeMenu, searchValue]
    )

    // Group items by category for display
    const groupedItems = React.useMemo(() => {
      const groups = {}
      filteredItems.forEach((item, index) => {
        const category = item.category || "Other"
        if (!groups[category]) {
          groups[category] = []
        }
        groups[category].push({ ...item, flatIndex: index })
      })
      return groups
    }, [filteredItems])

    const hasCategories = items.some((item) => item.category)

    return (
      <PlateElement
        as="span"
        className="inline-block"
        {...props}
      >
        <span ref={triggerRef} className={`inline-flex items-center gap-1 ${colors.badge} rounded px-1.5 py-0.5 font-medium`}>
          <AtSign className="size-3.5 shrink-0" contentEditable={false} />
          <input
            ref={inputRef}
            type="text"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-auto min-w-[1ch] max-w-[150px] bg-transparent outline-none"
            style={{ width: `${Math.max(1, searchValue.length)}ch` }}
          />
        </span>

        {/* Hidden children to maintain Slate structure */}
        <span className="hidden">{children}</span>

        {/* Dropdown - portaled to body to escape overflow containers */}
        {createPortal(
          <motion.div
            ref={dropdownRef}
            initial={{ opacity: 0, scale: 0.95, y: -8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.15 }}
            contentEditable={false}
            style={{ position: "fixed" }}
            className="z-50 min-w-[200px] max-h-[300px] overflow-y-auto rounded-md border bg-popover p-1 shadow-md scrollbar-styled"
          >
          {filteredItems.length > 0 ? (
            hasCategories ? (
              // Grouped display with categories
              Object.entries(groupedItems).map(([category, categoryItems], groupIndex) => (
                <div key={category}>
                  {groupIndex > 0 && (
                    <div className="bg-border -mx-1 my-1 h-px" />
                  )}
                  {categoryItems.map((item) => {
                    const Icon = item.icon
                    return (
                      <div
                        key={item.key}
                        className={cn(
                          "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors duration-150",
                          item.flatIndex === selectedIndex
                            ? "bg-accent text-accent-foreground"
                            : "hover:bg-accent hover:text-accent-foreground"
                        )}
                        onMouseDown={(e) => {
                          e.preventDefault()
                          handleSelect(item)
                        }}
                        onMouseEnter={() => setSelectedIndex(item.flatIndex)}
                      >
                        {Icon && <Icon className="size-4 text-muted-foreground" />}
                        <span>{item.text}</span>
                      </div>
                    )
                  })}
                </div>
              ))
            ) : (
              // Flat display without categories
              filteredItems.map((item, index) => {
                const Icon = item.icon
                return (
                  <div
                    key={item.key}
                    className={cn(
                      "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors duration-150",
                      index === selectedIndex
                        ? "bg-accent text-accent-foreground"
                        : "hover:bg-accent hover:text-accent-foreground"
                    )}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      handleSelect(item)
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    {Icon && <Icon className="size-4 text-muted-foreground" />}
                    <span>{item.text}</span>
                    {item.description && (
                      <span className="ml-auto text-xs text-muted-foreground truncate max-w-[150px]">{item.description}</span>
                    )}
                  </div>
                )
              })
            )
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </motion.div>,
          document.body
        )}
      </PlateElement>
    )
  }
}

// Slash command items
const SLASH_COMMAND_ITEMS = [
  {
    key: "ul",
    label: "Bulleted list",
    shortcut: "-",
    icon: ListIcon,
    action: (editor) => {
      toggleList(editor, { type: KEYS.ulClassic })
    },
  },
  {
    key: "ol",
    label: "Numbered list",
    shortcut: "1.",
    icon: ListOrderedIcon,
    action: (editor) => {
      toggleList(editor, { type: KEYS.olClassic })
    },
  },
  {
    key: "code",
    label: "Code block",
    shortcut: "```",
    icon: CodeXml,
    action: (editor) => {
      insertCodeBlock(editor)
    },
  },
  {
    key: "blockquote",
    label: "Quote",
    shortcut: ">",
    icon: MessageSquareQuote,
    action: (editor) => {
      editor.tf.setNodes({ type: KEYS.blockquote })
    },
  },
]

function SlashInputElement(props) {
  const { element, children } = props
  const editor = useEditorRef()
  const inputRef = React.useRef(null)
  const triggerRef = React.useRef(null)
  const dropdownRef = React.useRef(null)
  const [isOpen, setIsOpen] = React.useState(true)
  const [searchValue, setSearchValue] = React.useState("")
  const [selectedIndex, setSelectedIndex] = React.useState(0)
  usePortalPosition(triggerRef, dropdownRef)

  // Filter items based on search
  const filteredItems = SLASH_COMMAND_ITEMS.filter((item) =>
    item.label.toLowerCase().includes(searchValue.toLowerCase())
  )

  const closeMenu = React.useCallback(() => {
    setIsOpen(false)
    // Remove the slash input node
    editor.tf.removeNodes({
      at: [],
      match: (n) => n.type === "slash_input",
    })
  }, [editor])

  useClickOutside([triggerRef, dropdownRef], closeMenu)

  const handleSelect = React.useCallback(
    (item) => {
      // Remove the slash input node first
      editor.tf.removeNodes({
        at: [],
        match: (n) => n.type === "slash_input",
      })
      // Execute the action
      item.action(editor)
      // Refocus the editor after portal unmount
      requestAnimationFrame(() => editor.tf.focus())
    },
    [editor]
  )

  // Focus the input when mounted
  React.useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  // Reset selected index when filtered items change
  React.useEffect(() => {
    setSelectedIndex(0)
  }, [searchValue])

  // Handle keyboard navigation
  const handleKeyDown = React.useCallback(
    (e) => {
      if (e.key === "ArrowDown") {
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev < filteredItems.length - 1 ? prev + 1 : 0
        )
      } else if (e.key === "ArrowUp") {
        e.preventDefault()
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredItems.length - 1
        )
      } else if (e.key === "Enter") {
        e.preventDefault()
        if (filteredItems.length > 0) {
          handleSelect(filteredItems[selectedIndex])
        }
      } else if (e.key === "Escape") {
        e.preventDefault()
        closeMenu()
      } else if (e.key === "Backspace" && searchValue === "") {
        // When search is empty and user presses backspace, close menu and remove slash
        e.preventDefault()
        closeMenu()
        editor.tf.focus()
      }
    },
    [filteredItems, selectedIndex, handleSelect, closeMenu, searchValue, editor]
  )

  return (
    <PlateElement as="span" className="inline-block" {...props}>
      <span ref={triggerRef} className="inline-flex items-baseline text-muted-foreground">
        <span contentEditable={false}>/</span>
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="w-auto min-w-[1ch] max-w-[200px] bg-transparent outline-none text-foreground"
          style={{ width: `${Math.max(1, searchValue.length)}ch` }}
        />
      </span>

      {/* Hidden children to maintain Slate structure */}
      <span className="hidden">{children}</span>

      {/* Dropdown - portaled to body to escape overflow containers */}
      {isOpen && createPortal(
        <motion.div
          ref={dropdownRef}
          initial={{ opacity: 0, scale: 0.95, y: -8 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.15 }}
          contentEditable={false}
          style={{ position: "fixed" }}
          className="z-50 min-w-[220px] overflow-y-auto rounded-md border bg-popover p-1 shadow-md"
        >
          {filteredItems.length > 0 ? (
            filteredItems.map((item, index) => {
              const Icon = item.icon

              return (
                <div
                  key={item.key}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm transition-colors duration-150",
                    index === selectedIndex
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                  )}
                  onMouseDown={(e) => {
                    e.preventDefault()
                    handleSelect(item)
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                >
                  <Icon className="size-4 text-muted-foreground" />
                  <span>{item.label}</span>
                  <span className="ml-auto text-xs tracking-widest text-muted-foreground">
                    {item.shortcut}
                  </span>
                </div>
              )
            })
          ) : (
            <div className="px-2 py-1.5 text-sm text-muted-foreground">
              No results found
            </div>
          )}
        </motion.div>,
        document.body
      )}
    </PlateElement>
  )
}

const PlateEditor = React.forwardRef(function PlateEditor({
  id,
  value,
  onChange,
  onTab,
  onArrowUp,
  onArrowDown,
  placeholder = "Type here...",
  className,
  mentionItems,
  mentionColor = "orange",
  disableMentions = false,
  extractedValues,
  ...props
}, ref) {
  const contentRef = React.useRef(null)
  const initialValue = React.useMemo(() => parseValue(value), [])

  // Create custom mention components with field-specific items and color
  const CustomMentionElement = React.useMemo(
    () => createMentionElement(mentionColor),
    [mentionColor]
  )
  const CustomMentionInputElement = React.useMemo(
    () => createMentionInputElement(mentionItems, mentionColor),
    [mentionItems, mentionColor]
  )

  // Build plugins array based on configuration
  const plugins = React.useMemo(() => {
    const basePlugins = [
      // Basic marks
      BoldPlugin,
      ItalicPlugin,
      UnderlinePlugin,
      StrikethroughPlugin,

      // Blockquote
      BlockquotePlugin.configure({
        node: { component: BlockquoteElement },
      }),

      // Lists
      ListPlugin,
      ListItemContentPlugin,
      BulletedListPlugin.configure({
        node: { component: BulletedListElement },
      }),
      NumberedListPlugin.configure({
        node: { component: NumberedListElement },
      }),
      ListItemPlugin.configure({
        node: { component: ListItemElement },
      }),

      // Code blocks
      CodeBlockPlugin.configure({
        node: { component: CodeBlockElement },
      }),
      CodeLinePlugin.withComponent(CodeLineElement),

      // Slash commands
      SlashPlugin.configure({
        options: {
          trigger: "/",
          triggerPreviousCharPattern: /^\s?$/,
          triggerQuery: (editor) =>
            !editor.api.some({
              match: { type: editor.getType(KEYS.codeBlock) },
            }),
        },
      }),
      SlashInputPlugin.withComponent(SlashInputElement),

      // Autoformat for markdown-like shortcuts
      AutoformatPlugin.configure({
        options: {
          rules: autoformatRules,
          enableUndoOnDelete: true,
        },
      }),
    ]

    // Only add mention plugins if not disabled
    if (!disableMentions) {
      basePlugins.push(
        MentionPlugin.configure({
          options: {
            trigger: "@",
            triggerPreviousCharPattern: /^$|^[\s"']$/,
          },
        }).withComponent(CustomMentionElement),
        MentionInputPlugin.withComponent(CustomMentionInputElement)
      )
    }

    return basePlugins
  }, [disableMentions, CustomMentionElement, CustomMentionInputElement])

  const editor = usePlateEditor({
    value: initialValue,
    plugins,
  })

  React.useImperativeHandle(ref, () => ({
    focusAtStart: () => {
      editor.tf.focus({ edge: 'start' })
    },
    focusAtEnd: () => {
      editor.tf.focus({ edge: 'end' })
    },
  }), [editor])

  const handleKeyDown = (e) => {
    const { selection } = editor

    // Tab handling: move to next input (code block/list indentation handled by their plugins)
    if (e.key === "Tab" && !e.shiftKey) {
      const codeBlockType = editor.getType(KEYS.codeBlock)
      const inCodeBlock = editor.api.above({ match: { type: codeBlockType } })
      if (inCodeBlock) return

      const listItemType = editor.getType(KEYS.li)
      const inListItem = editor.api.above({ match: { type: listItemType } })
      if (inListItem) return

      if (onTab) {
        e.preventDefault()
        onTab()
      }
      return
    }

    if (!selection || !Range.isCollapsed(selection)) return

    // Enter on empty code line → exit the code block
    if (e.key === "Enter") {
      const codeLineType = editor.getType(KEYS.codeLine)
      const codeBlockType = editor.getType(KEYS.codeBlock)

      // Check if we're in a code line
      const codeLineEntry = editor.api.above({ match: { type: codeLineType } })
      if (codeLineEntry) {
        const [codeLine, codeLinePath] = codeLineEntry
        // Check if the code line is empty/whitespace-only (no meaningful text and no inline nodes like mentions)
        const lineText = Editor.string(editor, codeLinePath)
        const hasInlineNodes = codeLine.children.some((child) => child.type)
        if (lineText.trim() === "" && !hasInlineNodes) {
          // Check if we're in a code block
          const codeBlockEntry = editor.api.above({ match: { type: codeBlockType } })
          if (codeBlockEntry) {
            const [codeBlock, codeBlockPath] = codeBlockEntry
            // Only exit if there's more than one line (don't exit on first empty line)
            if (codeBlock.children.length > 1) {
              e.preventDefault()
              // Remove the empty code line
              editor.tf.removeNodes({ at: codeLinePath })
              // Insert a paragraph after the code block
              const afterPath = [...codeBlockPath.slice(0, -1), codeBlockPath[codeBlockPath.length - 1] + 1]
              editor.tf.insertNodes(
                { type: KEYS.p, children: [{ text: "" }] },
                { at: afterPath, select: true }
              )
              return
            }
          }
        }
      }
    }

    // ArrowUp/ArrowDown: check if the cursor is on the first/last visual
    // line BEFORE the browser handles the key. Compare the cursor's vertical
    // position against the editor's first/last text node position.
    if (e.key === "ArrowUp" && onArrowUp) {
      const sel = window.getSelection()
      if (sel?.rangeCount && contentRef.current) {
        const cursorTop = sel.getRangeAt(0).getBoundingClientRect().top
        const firstText = contentRef.current.querySelector("[data-slate-string], [data-slate-zero-width]")
        if (firstText) {
          const node = firstText.firstChild || firstText
          const range = document.createRange()
          range.setStart(node, 0)
          range.collapse(true)
          const firstLineTop = range.getBoundingClientRect().top
          if (Math.abs(cursorTop - firstLineTop) < 3) {
            e.preventDefault()
            onArrowUp()
          }
        }
      }
    }

    if (e.key === "ArrowDown" && onArrowDown) {
      const sel = window.getSelection()
      if (sel?.rangeCount && contentRef.current) {
        const cursorTop = sel.getRangeAt(0).getBoundingClientRect().top
        const allText = contentRef.current.querySelectorAll("[data-slate-string], [data-slate-zero-width]")
        const lastText = allText[allText.length - 1]
        if (lastText) {
          const node = lastText.firstChild || lastText
          const range = document.createRange()
          range.setStart(node, node.textContent?.length || 0)
          range.collapse(true)
          const lastLineTop = range.getBoundingClientRect().top
          if (Math.abs(cursorTop - lastLineTop) < 3) {
            e.preventDefault()
            onArrowDown()
          }
        }
      }
    }
  }

  return (
    <ExtractedValuesContext.Provider value={extractedValues ?? {}}>
      <Plate
        editor={editor}
        onChange={({ value: newValue }) => {
          const serialized = serializeValue(newValue)
          onChange?.(serialized)
        }}
        {...props}
      >
        <PlateContent
          ref={contentRef}
          id={id}
          placeholder={placeholder}
          onKeyDown={handleKeyDown}
          style={{ minHeight: 60 }}
          data-gramm="false"
          data-gramm_editor="false"
          data-enable-grammarly="false"
          className={cn(
            "min-h-[60px] w-full bg-transparent text-base outline-none md:text-sm",
            "[&_[data-slate-placeholder]]:text-muted-foreground [&_[data-slate-placeholder]]:!opacity-50",
            "[&_p]:m-0",
            "[&_strong]:font-bold",
            "[&_em]:italic",
            "[&_u]:underline",
            "[&_s]:line-through",
            className
          )}
        />
      </Plate>
    </ExtractedValuesContext.Provider>
  )
})

export { PlateEditor, parseValue, serializeValue, DATA_TYPE_ITEMS }
